import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom"; 
import { 
    Box, Button, Grid, Paper, Typography, 
    Switch, Chip, Divider, Avatar
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowLeft, faQrcode, faChartLine, faUsers, 
    faTag
} from "@fortawesome/free-solid-svg-icons";
import { getPromotionDetail, togglePromotionStatus } from "../../../controllers/company/loyalty/PromotionsController";
import QUDProgress from "../../../components/progress";


export default function PromotionDetailView() {
    const history = useHistory();
    const { id } = useParams();

    const [promo, setPromo] = useState();
    // Agregamos el estado para guardar la actividad real de la base de datos
    const [recentActivity, setRecentActivity] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        handleGetPromotionDetail();
    }, []);

    const redirect = (route) => {
        history.push(route);
    }

    const handleGetPromotionDetail = async () => {
        setIsLoading(true);
        const { promotion, recent_activity } = await getPromotionDetail(id);
        setPromo(promotion);
        setRecentActivity(recent_activity || []);
        setIsLoading(false);
    }

    const handleToggleStatus = async () => {
        // 1. Guardamos el estado original y calculamos el nuevo
        const previousStatus = promo.status;
        const newStatus = previousStatus === 'active' ? 'paused' : 'active';

        // 2. ACTUALIZACIÓN OPTIMISTA: Cambiamos el estado local instantáneamente.
        // Usamos el callback (prevPromo) para asegurar que tenemos la data más fresca.
        setPromo(prevPromo => ({ ...prevPromo, status: newStatus }));

        try {
            // 3. Hacemos la llamada al backend en segundo plano
            const response = await togglePromotionStatus(promo.id, newStatus);
            
            // Opcional: Si tu API siempre devuelve 200 pero con success: false, valídalo aquí.
            // if (response.success === false) throw new Error("El backend rechazó el cambio");

        } catch (error) {
            // 4. Si falla la red o el servidor da error, revertimos el switch silenciosamente
            setPromo(prevPromo => ({ ...prevPromo, status: previousStatus }));
            console.error("No se pudo cambiar el estatus de la promoción:", error);
        }
    };

    if (!promo || isLoading) {
        return <QUDProgress open={true}/>;
    }

    const isVip = promo.modality === 'qud_pass_exclusive';
    const isActive = promo.status === 'active';
    // Cálculo seguro de conversión
    const conversionRate = promo.claimed > 0 ? Math.round((promo.redeemed / promo.claimed) * 100) : 0;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'transparent' }}>
            
            {/* HEADER: Navegación y Estatus */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Button 
                        startIcon={<FontAwesomeIcon icon={faArrowLeft} />} 
                        onClick={() => history.goBack()}
                        disableRipple
                        sx={{ mb: 1, fontWeight: 600, color: '#888', textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', color: '#1565c0' } }}
                    >
                        Volver al listado
                    </Button>
                    <Typography variant="h4" sx={{ color: '#1a1a1a', fontWeight: 800, letterSpacing: -0.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                        Detalles de Campaña
                        <Chip 
                            label={isActive ? 'ACTIVA' : 'PAUSADA'} 
                            size="small"
                            sx={{ 
                                fontWeight: 800, 
                                fontSize: '0.7rem', 
                                letterSpacing: 0.5,
                                bgcolor: isActive ? 'rgba(21, 101, 192, 0.08)' : '#f5f5f5', 
                                color: isActive ? '#1565c0' : '#757575',
                                borderRadius: 1.5 
                            }} 
                        />
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', p: 1, px: 2.5, borderRadius: 3, border: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                        {isActive ? 'Campaña visible' : 'Campaña pausada'}
                    </Typography>
                    <Switch 
                        size="small" 
                        checked={promo.status === 'active'} 
                        onChange={handleToggleStatus} 
                        color="link" 
                    />
                </Box>
            </Box>

            <Grid container spacing={4}>
                
                {/* COLUMNA IZQUIERDA: Métricas */}
                <Grid item xs={12} md={8}>
                    
                    {/* Isla 1: Rendimiento Analítico (Sin barras de progreso) */}
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #f0f0f0', mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <FontAwesomeIcon icon={faChartLine} color="#1565c0" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                                Rendimiento de la Campaña
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', py: 3 }}>
                            <Box sx={{ flex: 1, borderRight: '1px solid #f0f0f0' }}>
                                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    CUPONES RECLAMADOS
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                    {promo.claimed || 0}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ flex: 1, px: 4, borderRight: '1px solid #f0f0f0' }}>
                                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    CUPONES CANJEADOS
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                    {promo.redeemed || 0}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1, pl: 4 }}>
                                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 700, letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    TASA DE CONVERSIÓN
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                    {conversionRate}%
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                   
                    {/* Isla 2: Información Técnica */}
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #f0f0f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            {/* Restaurado: Icono y texto en tu gris/azul original */}
                            <FontAwesomeIcon icon={faTag} color="#64748b" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                                Configuración del Beneficio
                            </Typography>
                        </Box>
                        
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                {/* Restaurado: Etiquetas en #888 y valores en #1a1a1a */}
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    Título Público
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                                    {promo.title}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    Valor Real
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                                    {promo.discount_details}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    Modalidad
                                </Typography>
                                {/* Restaurado: Colores originales para VIP (Naranja) y Público (Azul) */}
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: isVip ? '#f59e0b' : '#1565c0' }}>
                                    {isVip ? 'Exclusivo QUD Pass' : 'Público General'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                                    Vencimiento
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                                    {promo.valid_until_formatted}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* COLUMNA DERECHA: Acciones y Feed Real */}
                <Grid item xs={12} md={4}>
                    
                    <Button 
                        fullWidth 
                        variant="contained" 
                        disableElevation
                        startIcon={<FontAwesomeIcon icon={faQrcode} />}
                        sx={{ bgcolor: 'primary', py: 1.8, borderRadius: 3, fontWeight: 700, fontSize: '1rem', textTransform: 'none', mb: 4, transition: 'all 0.2s', '&:hover': { bgcolor: 'link.main', transform: 'translateY(-2px)', boxShadow: '0px 8px 20px rgba(21, 101, 192, 0.2)' } }}
                        onClick={()=> redirect(`/merchant/loyalty/scanner/`)}
                    >
                        Escanear Código
                    </Button>

                    <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #f0f0f0', bgcolor: '#f9f9f9' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FontAwesomeIcon icon={faUsers} color="#888" />
                            Actividad Reciente
                        </Typography>

                        {recentActivity.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 3 }}>
                                Aún no hay canjes registrados.
                            </Typography>
                        ) : (
                            recentActivity.map((item, index) => (
                                <Box key={item.id} sx={{ mb: index === recentActivity.length - 1 ? 0 : 2.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar 
                                                src={item.avatar_url} // <--- Si existe, MUI cargará la foto automáticamente
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36, 
                                                    bgcolor: '#f5f5f5', 
                                                    border: '1px solid #eaeaea', 
                                                    color: '#1a1a1a', 
                                                    fontSize: '0.85rem', 
                                                    fontWeight: 800 
                                                }}
                                            >
                                                {/* Si no hay foto, el componente mostrará la inicial automáticamente */}
                                                {!item.avatar_url && item.initial}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 500 }}>
                                                    {item.time}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                            {item.amount !== '-' ? item.amount : ''}
                                        </Typography>
                                    </Box>
                                    {index < recentActivity.length - 1 && <Divider sx={{ mt: 2.5, borderColor: '#eaeaea' }} />}
                                </Box>
                            ))
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}